/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ReadOnlyShareCardHeaderTitle } from '../components/ShareCardHeaderTitle';

import { IJewelryProvider } from '../../../../models/JewelryProvider';
import JewelryProviderSummary from './JewelryProviderSummary';
import ShareCardTemplate from '../components/ShareCardTemplate';
import CollapsibleRow from '../../../components/card/CollapsibleRow';
import { TrackEvent } from '../../../../utilities/UserMetrics';
import { useMedicalJewelry } from '../../../hooks/useMedicalJewelry';

interface ContentAreaProps {
  shareCardColorModel: any;
  onSelectProvider(d): void;
  supportedJewelryList: IJewelryProvider[];
  unupportedJewelryList: IJewelryProvider[];
}
const ContentArea: React.FC<ContentAreaProps> = (props) => {
  const { shareCardColorModel, onSelectProvider, supportedJewelryList, unupportedJewelryList } = props;

  const [showOutofCountryProviders, setShowOutofCountryProviders] = React.useState(() => (
    unupportedJewelryList.length === 1
  ));

  const outOfCountryMessageId = showOutofCountryProviders ? 'jewelryProviders.hideOtherProviders.message.text' : 'jewelryProviders.showOtherProviders.message.text';
  const hasAvailableJewelry = supportedJewelryList.length > 0;

  return (
    <>
      {
        hasAvailableJewelry && (
          <div className="font-subhead font-color-gsMedDark border-color-gsLight" style={{ padding: '18px 16px', borderBottomStyle: 'solid', borderBottomWidth: 1 }}>
            <FormattedMessage id="jewelryProviders.otherProvider.intro.text" />
          </div>
        )
      }

      {
        supportedJewelryList.length === 0
        ? (
          <div className="font-subhead font-color-gsMedDark border-color-gsLight" style={{ padding: '18px 24px', borderBottomStyle: 'solid', borderBottomWidth: 1 }}>
            <FormattedMessage id="jewelryProviders.noJewelryProviders.message.text" />
          </div>
        )
        : supportedJewelryList.map((mj, i) => (
          <div key={i} className="font-subhead font-color-gsMedDark border-color-gsLight" style={{ backgroundColor: 'white', padding: '18px 24px', borderBottomStyle: 'solid', borderBottomWidth: 1 }}>
            <JewelryProviderSummary
              data={mj}
              onRegister={() => { onSelectProvider(mj); }}
              isDefaultExpandend={supportedJewelryList.length === 1}
              cssColor={shareCardColorModel.darkAccentColor()}
              allowPurchase={false}
            />
          </div>
        ))
      }

      {
        unupportedJewelryList.length > 0 && (
          <div style={{ backgroundColor: 'white' }}>
            <CollapsibleRow
              title={(
                <div className="font-color-gsMedDark font-subhead">
                  <FormattedMessage id={outOfCountryMessageId} />
                </div>
              )}
              style={{ padding: '18px 24px' }}
              isExpanded={showOutofCountryProviders}
              onToggle={() => { setShowOutofCountryProviders(!showOutofCountryProviders); }}
              isCollapsible={true}
              defaultCollapsed={true}
              hasBorder={true}
            >
              {
                unupportedJewelryList.map((mj, i) => (
                  <div key={i} className="font-subhead font-color-gsMedDark border-color-gsLight" style={{ backgroundColor: 'white', padding: '18px 24px', borderBottomStyle: 'solid', borderBottomWidth: 1 }}>
                    <JewelryProviderSummary
                      data={mj}
                      onRegister={() => { onSelectProvider(mj); }}
                      isDefaultExpandend={unupportedJewelryList.length === 1}
                      cssColor={shareCardColorModel.darkAccentColor()}
                      allowPurchase={false}
                    />
                  </div>
                ))
              }
            </CollapsibleRow>
          </div>
        )
      }

      <div className="font-subhead font-color-gsMedDark" style={{ padding: '18px 16px' }}>
        <FormattedMessage id="jewelryProviders.otherProvider.message.text" />
      </div>
    </>
  );
};

export interface Props {
  shareCardColorModel: any;
  shareCardTemplateUid: string;
  shareCardId: number;
  onSelectProvider(provider: IJewelryProvider): void;
  onCancel(): void;
}

const JewelryProviderList: React.FC<Props> = (props) => {
  const { shareCardColorModel, shareCardTemplateUid, onCancel, shareCardId, onSelectProvider } = props;
  const [{ supportedJewelryList, unupportedJewelryList, isLoaded }] = useMedicalJewelry();

  return (
    <ShareCardTemplate
      shareCardColorModel={shareCardColorModel}
      disableScrolling={false}
      scrollAwareHeader={(
        <ReadOnlyShareCardHeaderTitle
          isGroupAccessible={false}
          cssColor={shareCardColorModel.cssColor(1)}
          title={<FormattedMessage id="jewelryProviders.title.text" />}
          onCancel={onCancel}
          isSubmitDisabled={false}
        />
      )}
      shareCardFooter={null}
      isContentLoaded={isLoaded}
      shareCardId={shareCardId}
    >
      {
        isLoaded ? (
          <ContentArea
            supportedJewelryList={supportedJewelryList}
            unupportedJewelryList={unupportedJewelryList}
            shareCardColorModel={shareCardColorModel}
            onSelectProvider={(jewelryProvider) => {
              TrackEvent.shareCards.registerStickyJ(shareCardTemplateUid);
              onSelectProvider(jewelryProvider);
            }}
          />
        ) : null
      }
    </ShareCardTemplate>
  );
};

export default JewelryProviderList;
